import React, { useCallback, useEffect, useRef, useState } from "react";
import { DropdownEntry } from "../config/types";

export const useDropdownToggle = (
  dropdownId: string,
  disabled?: boolean,
  onContainerClick?: () => void,
  autoOpen?: boolean
) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [rect, setRect] = useState<DOMRect | false>(false);

  const onOpen = useCallback(
    (event: React.MouseEvent) => {
      if (disabled) return;
      if (rect) {
        setRect(false);
        return;
      }
      const clickedElement = event.target as HTMLElement;
      const container =
        (clickedElement.closest(`#${dropdownId}`) as HTMLElement) ||
        containerRef.current;
      if (!container) return;
      setRect(container.getBoundingClientRect());
    },
    [disabled, dropdownId, rect]
  );

  const onContainerClickHandler = useCallback(
    (event: React.MouseEvent) => {
      if (!onContainerClick || disabled || rect) return;
      const clickedElement = event.target as HTMLElement;
      if (clickedElement.tagName !== "BUTTON" && !clickedElement.closest("button")) {
        onContainerClick();
        event.stopPropagation();
      }
    },
    [disabled, rect, onContainerClick]
  );

  const onContainerKeyDownHandler = useCallback(
    (event: React.KeyboardEvent) => {
      if (!onContainerClick || disabled || rect) return;
      if ((event.key === "Enter" || event.key === " ") && onContainerClick) {
        const target = event.target as HTMLElement;
        if (target.tagName !== "BUTTON" && !target.closest("button")) {
          event.preventDefault();
          onContainerClick();
          event.stopPropagation();
        }
      }
    },
    [disabled, rect, onContainerClick]
  );

  useEffect(() => {
    if (!autoOpen || rect) return;
    const element = containerRef.current;
    if (element) {
      setRect(element.getBoundingClientRect());
    }
  }, [autoOpen, rect]);

  useEffect(() => {
    if (!rect) return undefined;

    const handleOutsideInteraction = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      const overlay = target.closest("#clava-dropdown-overlay");
      const trigger = target.closest(`#${dropdownId}`);
      if (overlay || trigger) return;
      setRect(false);
    };

    const handleScroll = (event: Event) => {
      const { target } = event;
      if (target instanceof HTMLElement && target.closest("#clava-dropdown-overlay")) {
        return;
      }
      setRect(false);
    };

    const handleResize = () => {
      setRect(false);
    };

    document.addEventListener("mousedown", handleOutsideInteraction, true);
    document.addEventListener("touchstart", handleOutsideInteraction, true);
    document.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize, true);

    return () => {
      document.removeEventListener("mousedown", handleOutsideInteraction, true);
      document.removeEventListener("touchstart", handleOutsideInteraction, true);
      document.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize, true);
    };
  }, [rect, dropdownId, setRect]);

  return {
    containerRef,
    rect,
    setRect,
    onOpen,
    onContainerClickHandler,
    onContainerKeyDownHandler,
  };
};
export const useDropdownOverlay = (
  rect: DOMRect | false,
  dropdownId: string,
  overlayRenderer: (style: React.CSSProperties) => React.ReactElement,
  setOpen: (entry: DropdownEntry) => void,
  setClose: (id?: string) => void,
  options?: { withBlur?: boolean; minWidth?: number; maxWidth?: number; width?: number }
) => {
  const withBlur = options?.withBlur ?? false;
  const minWidth = options?.minWidth;
  const maxWidth = options?.maxWidth;
  const explicitWidth = options?.width;

  useEffect(() => {
    if (!rect) {
      setClose(dropdownId);
      return undefined;
    }
    let width = explicitWidth ?? Math.max(rect.width, minWidth ?? rect.width);
    if (typeof maxWidth === "number") {
      width = Math.min(width, maxWidth);
    }
    const style = {
      top: rect.bottom,
      left: rect.left,
      position: "fixed",
      width,
    } as React.CSSProperties;
    setOpen({
      id: dropdownId,
      node: overlayRenderer(style),
      withBlur,
    });
    return () => {
      setClose(dropdownId);
    };
  }, [rect, dropdownId, overlayRenderer, setOpen, setClose, withBlur, explicitWidth, minWidth, maxWidth]);
};

